import React from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
interface SupplierOption {
  id: string;
  name: string;
}

const CHUNK_SIZE = 5 * 1024 * 1024;

function genUploadId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function classifyFileType(name: string): 'article' | 'datpreis' | 'minuten' | 'unknown' {
  const upper = name.toUpperCase();
  if (upper.includes('DATPREIS')) return 'datpreis';
  if (upper.includes('DATASET')) return 'unknown';
  if (upper.endsWith('.999') || upper.includes('MINUTEN')) return 'minuten';
  if (upper.endsWith('.001')) return 'article';
  return 'unknown';
}

function pickArticleFile(files: File[]): File | null {
  const articles = files.filter((f) => classifyFileType(f.name) === 'article');
  if (articles.length === 0) return null;
  return articles.find((f) => /datanorm/i.test(f.name)) ?? articles[0];
}

async function uploadFileInChunks(file: File): Promise<string> {
  const uploadId = genUploadId();
  for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
    const slice = file.slice(offset, offset + CHUNK_SIZE);
    const buf = await slice.arrayBuffer();
    const res = await fetch(
      `/api/admin/datanorm/chunks?uploadId=${encodeURIComponent(uploadId)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: buf,
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Upload-Fehler (${res.status}): ${text.slice(0, 200)}`);
    }
  }
  return uploadId;
}

export function AdminDatanormTab() {
  const [suppliers, setSuppliers] = React.useState<SupplierOption[]>([]);
  const [supplierId, setSupplierId] = React.useState<string>('');
  const [files, setFiles] = React.useState<File[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<{
    totalImported: number;
    totalDeleted: number;
    skippedUnknownUnit: number;
    supplierName: string;
  } | null>(null);

  const loadSuppliers = React.useCallback(() => {
    return fetch('/api/suppliers?all=1', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        const items = Array.isArray(data?.items) ? (data.items as SupplierOption[]) : [];
        setSuppliers(items);
        if (items.length > 0 && !supplierId) setSupplierId(items[0].id);
        return items;
      })
      .catch(() => [] as SupplierOption[]);
  }, [supplierId]);

  React.useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const articleFile = pickArticleFile(files);
  const datpreisFiles = files.filter((f) => classifyFileType(f.name) === 'datpreis');
  const minutenFiles = files.filter((f) => classifyFileType(f.name) === 'minuten');

  const handleFiles = (next: FileList | null) => {
    if (!next) return;
    const accepted: File[] = [];
    for (let i = 0; i < next.length; i++) {
      const f = next.item(i);
      if (!f) continue;
      const t = classifyFileType(f.name);
      if (t !== 'unknown') accepted.push(f);
    }
    setFiles(accepted);
  };

  const handleImport = async () => {
    if (!supplierId) {
      setError('Bitte einen Großhändler wählen.');
      return;
    }
    if (!articleFile) {
      setError('Bitte eine .001-Artikeldatei hochladen.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const articleUploadId = await uploadFileInChunks(articleFile);
      const datpreisUploadIds: string[] = [];
      for (const f of datpreisFiles) datpreisUploadIds.push(await uploadFileInChunks(f));
      const minutenUploadIds: string[] = [];
      for (const f of minutenFiles) minutenUploadIds.push(await uploadFileInChunks(f));

      const res = await fetch('/api/admin/datanorm/import-supplier-catalog', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplierId,
          articleUploadId,
          datpreisUploadIds,
          minutenUploadIds,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as any)?.error || 'Import fehlgeschlagen.');
      const supplierName = suppliers.find((s) => s.id === supplierId)?.name ?? '';
      setResult({
        totalImported: Number((data as any)?.totalImported ?? 0),
        totalDeleted: Number((data as any)?.totalDeleted ?? 0),
        skippedUnknownUnit: Number((data as any)?.skippedUnknownUnit ?? 0),
        supplierName,
      });
      setFiles([]);
      await loadSuppliers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-app-text-muted leading-relaxed bg-app-surface-muted border border-app-border rounded-lg p-3">
        Großhändler-Kataloge werden zentral verwaltet und stehen anschließend allen Instanzen zur
        Verfügung. Hochgeladene DATANORM-Daten landen in <code>supplier_materials</code> und werden
        automatisch in den globalen Vektor-Index aufgenommen.
      </p>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-app-text-muted">Großhändler</label>
        <select
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          className="w-full border border-app-border rounded-md px-3 py-1.5 text-sm bg-app-surface"
          disabled={busy || suppliers.length === 0}
        >
          {suppliers.length === 0 && <option value="">Bitte zuerst einen Großhändler anlegen.</option>}
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-app-text-muted">DATANORM-Dateien</label>
        <label className="block border-2 border-dashed border-app-border-strong rounded-lg p-6 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/30 transition-colors">
          <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-app-text-muted">Klicken zum Hochladen</p>
          <p className="text-[10px] text-slate-400 mt-1">.001 (Artikel), DATPREIS, .999 / MINUTEN</p>
          <input
            type="file"
            multiple
            accept=".001,.999,*"
            className="hidden"
            disabled={busy}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
        {files.length > 0 && (
          <ul className="border border-app-border rounded-lg overflow-hidden">
            {files.map((f) => {
              const t = classifyFileType(f.name);
              return (
                <li
                  key={f.name + f.size}
                  className="flex items-center gap-2 px-3 py-2 text-xs border-b last:border-b-0 border-app-border bg-app-surface"
                >
                  <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="px-1.5 py-0.5 rounded bg-app-surface-muted text-app-text-subtle text-[9px] uppercase">
                    {t === 'article' ? 'Artikel' : t === 'datpreis' ? 'Preise' : t === 'minuten' ? 'Minuten' : '?'}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFiles((prev) => prev.filter((x) => !(x.name === f.name && x.size === f.size)))
                    }
                    className="p-0.5 rounded hover:bg-app-hover"
                    disabled={busy}
                  >
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div
          className={`px-3 py-2 rounded-lg border text-sm flex items-start gap-2 ${
            result.totalImported > 0
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          {result.totalImported > 0 ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span>
            {result.totalImported > 0
              ? `${result.totalImported.toLocaleString('de-DE')} Artikel zu „${result.supplierName}“ importiert`
              : `Keine Artikel zu „${result.supplierName}“ importiert. Der Server hat in der Datei keine verwertbaren A-Sätze gefunden (falsche Datei oder Parser noch nicht neu gestartet).`}
            {result.totalDeleted > 0
              ? `, ${result.totalDeleted.toLocaleString('de-DE')} Löschsätze entfernt`
              : ''}
            {result.skippedUnknownUnit > 0
              ? `, ${result.skippedUnknownUnit.toLocaleString('de-DE')} wegen unbekannter Einheit übersprungen`
              : ''}
            {result.totalImported > 0 ? '. Vektor-Indexierung läuft im Hintergrund.' : ''}
          </span>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleImport()}
          disabled={busy || !supplierId || !articleFile}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-violet-600 text-white text-sm font-medium disabled:opacity-50 hover:bg-violet-700"
        >
          {busy && <LoadingSpinner />}
          Import starten
        </button>
      </div>
    </div>
  );
}
